import type { AxiosInstance } from 'axios'
import type {
  AcademyLang,
  IAcademyCatalog,
  IAcademyLesson,
  IAcademyProgressUpdate,
  IFactoryFunctionRequest,
} from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import useAxios from '@/services/api/useAxios'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import { useUserStore } from '@/config/store/userStore'
import type {
  TAcademyProgressResponse,
  TAcademyQuizAnswerResponse,
  TAcademySummary,
  TAcademyTeamMemberResponse,
  TAcademyTeamQuery,
  TAcademyTeamResponse,
  TAcademyVisit,
} from '@/services/academy/types'

const resource = 'academy'
const baseUrl = () => `${BASE_URL}/${API_MAIN_VERSION}/${resource}`

/** Published lessons (no pages) with the learner's status of each. */
const catalog = (axios: IFactoryFunctionRequest<IAcademyCatalog, any>) => (query: { lang: AcademyLang }) => {
  return axios({
    method: EMethod.get,
    url: `${baseUrl()}/catalog`,
    query,
  })
}

const summary = (axios: IFactoryFunctionRequest<TAcademySummary, any>) => () => {
  return axios({
    method: EMethod.get,
    url: `${baseUrl()}/summary`,
  })
}

const lesson = (axios: IFactoryFunctionRequest<IAcademyLesson, any>) => (slug: string, query: { lang: AcademyLang }) => {
  return axios({
    method: EMethod.get,
    url: `${baseUrl()}/lessons/${encodeURIComponent(slug)}`,
    query,
  })
}

/** The client sends cumulative values: the API keeps the max of each, so a resent update changes nothing. */
const updateProgress = (axios: IFactoryFunctionRequest<TAcademyProgressResponse, IAcademyProgressUpdate>) => (slug: string, data: IAcademyProgressUpdate) => {
  return axios({
    method: EMethod.put,
    url: `${baseUrl()}/progress/${encodeURIComponent(slug)}`,
    data,
  })
}

const answerQuiz = (axios: IFactoryFunctionRequest<TAcademyQuizAnswerResponse, any>) => (slug: string, blockKey: string, data: { selectedAnswerIds: string[], lang: AcademyLang }) => {
  return axios({
    method: EMethod.post,
    url: `${baseUrl()}/progress/${encodeURIComponent(slug)}/quizzes/${encodeURIComponent(blockKey)}`,
    data,
  })
}

/** Call once per academy home visit, before the catalog: it rolls `previousVisitAt` forward, which `isNew` is computed against. */
const visit = (axios: IFactoryFunctionRequest<TAcademyVisit, any>) => () => {
  return axios({
    method: EMethod.post,
    url: `${baseUrl()}/visit`,
  })
}

const team = (axios: IFactoryFunctionRequest<TAcademyTeamResponse, any>) => (query: TAcademyTeamQuery) => {
  return axios({
    method: EMethod.get,
    url: `${baseUrl()}/team`,
    query,
  })
}

/** Lesson titles come in `lang`. */
const teamMember = (axios: IFactoryFunctionRequest<TAcademyTeamMemberResponse, any>) => (resourceId: string, query: { lang: AcademyLang }) => {
  return axios({
    method: EMethod.get,
    url: `${baseUrl()}/team/${encodeURIComponent(resourceId)}`,
    query,
  })
}

/**
 * Same update as `updateProgress`, sent while the page is being hidden or closed: a keepalive
 * fetch outlives the page, an axios request may be cancelled with it.
 */
export const sendAcademyProgressKeepalive = (slug: string, data: IAcademyProgressUpdate) => {
  const token = useUserStore().token
  if (!token) return
  fetch(`${baseUrl()}/progress/${encodeURIComponent(slug)}`, {
    method: 'PUT',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      'authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  }).catch(() => {})
}

export default (axios: AxiosInstance) => ({
  catalog: catalog(useAxios(axios)),
  summary: summary(useAxios(axios)),
  lesson: lesson(useAxios(axios)),
  updateProgress: updateProgress(useAxios(axios)),
  answerQuiz: answerQuiz(useAxios(axios)),
  visit: visit(useAxios(axios)),
  team: team(useAxios(axios)),
  teamMember: teamMember(useAxios(axios)),
})
